import { useState, type KeyboardEvent } from 'react';
import { ChevronDownIcon, ChevronRightIcon } from 'lucide-react';
import { CONTEXT_ID_ATTRIBUTE, ContextMenuArea, type ContextMenuSpec } from '../actions/context-menu-area';
import { cn } from '../lib/utils';

export interface TreeNode {
  id: string;
  label: string;
  children?: TreeNode[];
  expandable?: boolean;
}
export interface TreeProps {
  label: string;
  nodes: readonly TreeNode[];
  selected?: string;
  onSelect: (id: string) => void;
  onExpand: (id: string) => Promise<void> | void;
  /** Context menu per node (right-click, Shift+F10, long-press; DESIGN.md §18). */
  menuFor?: (id: string) => ContextMenuSpec | undefined;
}
/** Lazy accessible tree with roving focus; callers own fetched children and labels. */
export function Tree({ label, nodes, selected, onSelect, onExpand, menuFor }: TreeProps) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [focused, setFocused] = useState<string>();
  const rows: { node: TreeNode; level: number; parent?: string }[] = [];
  const visit = (items: readonly TreeNode[], level: number, parent?: string) => {
    for (const node of items) {
      rows.push({ node, level, parent });
      if (expanded.has(node.id)) visit(node.children ?? [], level + 1, node.id);
    }
  };
  visit(nodes, 1);
  const toggle = async (node: TreeNode) => {
    if (expanded.has(node.id))
      setExpanded((current) => {
        const next = new Set(current);
        next.delete(node.id);
        return next;
      });
    else {
      await onExpand(node.id);
      setExpanded((current) => new Set([...current, node.id]));
    }
  };
  const key = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const row = rows[index]!;
    let next: string | undefined;
    if (event.key === 'ArrowDown') next = rows[index + 1]?.node.id;
    else if (event.key === 'ArrowUp') next = rows[index - 1]?.node.id;
    else if (event.key === 'Home') next = rows[0]?.node.id;
    else if (event.key === 'End') next = rows.at(-1)?.node.id;
    else if (event.key === 'ArrowRight') {
      if (!expanded.has(row.node.id) && row.node.expandable) void toggle(row.node);
      else next = rows[index + 1]?.level === row.level + 1 ? rows[index + 1]?.node.id : undefined;
    } else if (event.key === 'ArrowLeft') {
      if (expanded.has(row.node.id)) void toggle(row.node);
      else next = row.parent;
    } else return;
    event.preventDefault();
    if (next) {
      setFocused(next);
      const buttons = event.currentTarget.closest('[role=tree]')?.querySelectorAll<HTMLButtonElement>('[role=treeitem]');
      buttons?.[rows.findIndex((r) => r.node.id === next)]?.focus();
    }
  };
  return (
    <ContextMenuArea menuFor={menuFor}>
    <div role="tree" aria-label={label} className="flex flex-col gap-0.5">
      {rows.map(({ node, level }, index) => (
        <button
          type="button"
          key={node.id}
          {...{ [CONTEXT_ID_ATTRIBUTE]: node.id }}
          role="treeitem"
          aria-level={level}
          aria-expanded={node.expandable ? expanded.has(node.id) : undefined}
          aria-selected={selected === node.id}
          tabIndex={(focused ?? selected ?? rows[0]?.node.id) === node.id ? 0 : -1}
          onFocus={() => setFocused(node.id)}
          onKeyDown={(event) => key(event, index)}
          onClick={() => {
            onSelect(node.id);
            if (node.expandable) void toggle(node);
          }}
          style={{ paddingInlineStart: `${0.75 + (level - 1) * 1.25}rem` }}
          className={cn(
            'flex min-h-row items-center gap-2 rounded-md py-1 pe-3 text-left text-sm focus-visible:ring-2 focus-visible:ring-ring',
            selected === node.id && 'bg-muted',
          )}
        >
          {node.expandable ? (
            expanded.has(node.id) ? <ChevronDownIcon aria-hidden className="size-4 shrink-0" /> : <ChevronRightIcon aria-hidden className="size-4 shrink-0" />
          ) : (
            <span aria-hidden className="size-4 shrink-0" />
          )}
          {node.label}
        </button>
      ))}
    </div>
    </ContextMenuArea>
  );
}
